import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Button, Avatar, Chip, Stack, Divider } from '@mui/material';
import { FiFilter, FiX } from 'react-icons/fi';

import Actions from '../../constants/Actions';
import { deriveFilterOptions, resolveFilterOptions, countActiveFilters, mapFilialOptions } from '../../utils/filterOptions';
import FilterDropdown from './FilterDropdown';
import PriceDropdown from './PriceDropdown';

const ProductFiltersBar = () => {
    const dispatch = useDispatch();
    const products = useSelector((state) => state.products);
    const filters = useSelector((state) => state.filters);
    const filialOptions = useSelector((state) => state.filialOptions);
    const filiaisDisponiveis = useSelector((state) => state.filiaisDisponiveis);
    const filterFacets = useSelector((state) => state.filterFacets);

    const derivedFromProducts = React.useMemo(() => deriveFilterOptions(products), [products]);
    const derived = React.useMemo(
        () => resolveFilterOptions(filterFacets, derivedFromProducts),
        [filterFacets, derivedFromProducts]
    );
    const active = countActiveFilters(filters);

    const setFilter = (key, value) => {
        dispatch({ type: Actions.SET_FILTER, key, value });
    };

    const handleReset = () => {
        dispatch({ type: Actions.RESET_FILTERS });
    };

    const handlePriceChange = ({ precoMin, precoMax }) => {
        dispatch({ type: Actions.SET_FILTER, key: 'precoMin', value: precoMin });
        dispatch({ type: Actions.SET_FILTER, key: 'precoMax', value: precoMax });
    };

    const filialMapped = React.useMemo(
        () => mapFilialOptions(filialOptions, filiaisDisponiveis),
        [filialOptions, filiaisDisponiveis]
    );

    const filialLabelByValue = React.useMemo(() => {
        const map = new Map();
        filialMapped.forEach((f) => map.set(f.value, f.label));
        return map;
    }, [filialMapped]);

    const removeFromList = (key, value) => {
        const list = Array.isArray(filters[key]) ? filters[key] : [];
        setFilter(key, list.filter((v) => v !== value));
    };

    const clearPrice = () => {
        setFilter('precoMin', null);
        setFilter('precoMax', null);
    };

    const chipSx = {
        bgcolor: '#1a1a2e',
        color: '#fff',
        fontSize: 11,
        fontWeight: 600,
        height: 22,
        '& .MuiChip-deleteIcon': { color: '#fff', mr: 0.5 },
        '& .MuiChip-deleteIcon:hover': { color: '#ddd' }
    };

    const renderChips = (key, list, labelResolver) => {
        if (!Array.isArray(list) || list.length === 0) return null;
        return list.map((value) => (
            <Chip
                key={`${key}-${value}`}
                label={labelResolver ? labelResolver(value) : value}
                size="small"
                onDelete={() => removeFromList(key, value)}
                deleteIcon={<FiX size={12} />}
                sx={chipSx}
            />
        ));
    };

    const hasPriceActive =
        (filters.precoMin !== null && filters.precoMin !== undefined)
        || (filters.precoMax !== null && filters.precoMax !== undefined);

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                px: { xs: 1.25, md: 2 },
                py: 1.25,
                position: 'sticky',
                top: 0,
                zIndex: 5
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                    sx={{
                        display: { xs: 'none', md: 'flex' },
                        alignItems: 'center',
                        gap: 1,
                        flexShrink: 0,
                        pr: 1
                    }}
                >
                    <Avatar sx={{ width: 28, height: 28, bgcolor: '#e8eaf0', color: '#1a1a2e' }}>
                        <FiFilter size={14} />
                    </Avatar>
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Filtros</Typography>
                    {active > 0 && (
                        <Box
                            sx={{
                                px: 0.75,
                                minWidth: 20,
                                height: 20,
                                borderRadius: 10,
                                bgcolor: '#1a1a2e',
                                color: '#fff',
                                fontSize: 11,
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            {active}
                        </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        overflowX: 'auto',
                        py: 0.25,
                        '&::-webkit-scrollbar': { height: 4 },
                        '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 2 }
                    }}
                >
                    <FilterDropdown
                        title="Departamento"
                        options={derived.departamento}
                        selected={filters.departamento}
                        onChange={(v) => setFilter('departamento', v)}
                    />
                    <FilterDropdown
                        title="Griffe"
                        options={derived.griffe}
                        selected={filters.griffe}
                        onChange={(v) => setFilter('griffe', v)}
                    />
                    <FilterDropdown
                        title="Grupo"
                        options={derived.grupo}
                        selected={filters.grupo}
                        onChange={(v) => setFilter('grupo', v)}
                    />
                    <FilterDropdown
                        title="Subgrupo"
                        options={derived.subgrupo}
                        selected={filters.subgrupo}
                        onChange={(v) => setFilter('subgrupo', v)}
                    />
                    <FilterDropdown
                        title="Grade"
                        options={derived.grade}
                        selected={filters.grade}
                        onChange={(v) => setFilter('grade', v)}
                    />
                    <FilterDropdown
                        title="Filial"
                        groupLabel="Cidade"
                        options={filialMapped}
                        selected={filters.filial}
                        onChange={(v) => setFilter('filial', v)}
                    />
                    <PriceDropdown
                        precoMin={filters.precoMin}
                        precoMax={filters.precoMax}
                        onChange={handlePriceChange}
                    />
                </Box>

                {active > 0 && (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleReset}
                        startIcon={<FiX size={12} />}
                        sx={{
                            flexShrink: 0,
                            textTransform: 'none',
                            fontSize: 11,
                            fontWeight: 600,
                            height: 34,
                            px: 1,
                            borderRadius: 2,
                            borderColor: '#1a1a2e',
                            color: '#1a1a2e',
                            '&:hover': { borderColor: '#1a1a2e', bgcolor: '#f0f1f5' }
                        }}
                    >
                        Limpar
                    </Button>
                )}
            </Box>

            {active > 0 && (
                <>
                    <Divider sx={{ my: 1 }} />
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center">
                        <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 600, mr: 0.5 }}>
                            Selecionados ({active}):
                        </Typography>
                        {renderChips('departamento', filters.departamento)}
                        {renderChips('griffe', filters.griffe)}
                        {renderChips('grupo', filters.grupo)}
                        {renderChips('subgrupo', filters.subgrupo)}
                        {renderChips('grade', filters.grade)}
                        {renderChips('filial', filters.filial, (v) => filialLabelByValue.get(v) || v)}
                        {hasPriceActive && (
                            <Chip
                                key="preco"
                                label={`R$ ${filters.precoMin ?? 0} - ${filters.precoMax ?? '∞'}`}
                                size="small"
                                onDelete={clearPrice}
                                deleteIcon={<FiX size={12} />}
                                sx={chipSx}
                            />
                        )}
                    </Stack>
                </>
            )}
        </Box>
    );
};

export default ProductFiltersBar;
